<template>
    <div
        :id="`kid-score-${well._id}`"
        class="kid-score"
        :class="[option, kidScoreColor, { disabled: !kidScore }]"
    >
        <b-tooltip
            :show.sync="showTooltip"
            :target="`kid-score-${well._id}`"
            placement="bottom"
            triggers="click"
        >
            {{ $translate('feature_not_available') }}
        </b-tooltip>

        <div v-if="isDuc && !showDucInsteadScore" class="duc-badge">
            <div class="bg"></div>
            <span>D</span>
        </div>

        <span class="label">{{ $translate('eq_score') }}</span>

        <div class="value">
            <span v-if="formattedKidScore" class="score">{{ formattedKidScore }}</span>

            <span v-else-if="showNA" class="na">{{ $translate('na') }}</span>

            <span v-else-if="hazards" class="score-hazard">
                <img
                    v-if="degHazard"
                    src="/static/svg/Hazard_Deg_white.svg"
                    alt=""
                    :title="$translate('degenerated_oocyte')"
                />

                <img
                    v-else-if="pnHazard"
                    src="/static/svg/Hazard_PN_white.svg"
                    alt=""
                    :title="$uiUtils.formatPnHazardMessage(pnCount)"
                />

                <img
                    v-else-if="showDucInsteadScore"
                    src="/static/svg/Hazard_Duc_white.svg"
                    alt=""
                    :title="$translate('duc_tooltip')"
                />
            </span>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import UIUtils from '../../../business/utils/UIUtils'
import { MAX_WELL_TIME_HOURS } from '../../../business/const'

export default {
    name: 'KidScore',
    props: {
        well: {
            type: Object,
            required: true
        },
        option: {
            type: String,
            required: true
        },
        currentWellTimeHours: {
            type: Number,
            default: null
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        colorBackground: {
            type: Boolean,
            default: true
        },
        onlyBackgroundHighScore: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        showTooltip: false
    }),
    computed: {
        ...mapState({
            selectedTenant: state => state.common.selectedTenant
        }),
        ...mapGetters({
            getPnCount: 'timelapse/getPnCount',
            getPnHazard: 'timelapse/getPnHazard',
            checkDucsHazard: 'timelapse/checkDucsHazard',
            checkDegeneratedOocyteHazard: 'timelapse/checkDegeneratedOocyteHazard'
        }),
        pnCount() {
            return this.getPnCount(this.well)
        },
        pnHazard() {
            return this.getPnHazard(this.well)
        },
        degHazard() {
            return this.checkDegeneratedOocyteHazard(this.well)
        },
        isDuc() {
            return this.checkDucsHazard(this.well)
        },
        showDucInsteadScore() {
            if (this.isDuc && this.well.excludeFromRanking === undefined) {
                return true
            }
            return this.isDuc && this.well.excludeFromRanking
        },
        inferenceData() {
            return this.well.inferenceData
        },
        isByCurrentHour() {
            return !!this.currentWellTimeHours
        },
        currentHourEven() {
            if (this.isByCurrentHour) {
                const hours =
                    this.currentWellTimeHours <= MAX_WELL_TIME_HOURS
                        ? this.currentWellTimeHours
                        : MAX_WELL_TIME_HOURS

                return 2 * Math.ceil(hours / 2)
            }

            return null
        },
        kidScore() {
            if (!this.isByCurrentHour) {
                return this.inferenceData.kidScore
            }

            const { kidScoreOverTime } = this.inferenceData
            const firstKey = Object.keys(kidScoreOverTime).find(x => !!x)
            const key = firstKey % 2 === 1 ? this.currentHourEven - 1 : this.currentHourEven

            return kidScoreOverTime && kidScoreOverTime[key]
        },
        formattedKidScore() {
            return UIUtils.formatScore(this.kidScore)
        },
        kidScoreColor() {
            return UIUtils.getKidScoreColor(
                this.selectedTenant,
                this.kidScore,
                this.colorBackground,
                this.onlyBackgroundHighScore
            )
        },
        hazards() {
            return this.pnHazard || this.degHazard || this.showDucInsteadScore
        },
        showNA() {
            return !this.kidScore && !this.hazards
        }
    },
    watch: {
        showTooltip(shouldShow) {
            if (shouldShow) {
                setTimeout(() => {
                    this.showTooltip = false
                }, 2000)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.kid-score {
    // relevant to all options
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    border-radius: $border-radius;

    .duc-badge {
        position: absolute;
        width: 2rem;
        height: 2rem;
        top: -1px;
        left: -1px;

        .bg {
            width: 100%;
            height: 100%;
            border-style: solid;
            border-width: 1rem; // half size
            border-top-color: $duc;
            border-left-color: $duc;
            border-bottom-color: transparent;
            border-right-color: transparent;
            border-top-left-radius: $border-radius;
        }

        span {
            position: absolute;
            top: 10%;
            left: 15%;
            color: $dark-4;
            font-size: 10px;
            font-weight: 600;
        }
    }

    .score-hazard img {
        height: 1.4rem;
    }

    &.rag-red-text {
        color: $red !important;
    }
    &.rag-amber-text {
        color: $orange !important;
    }
    &.rag-green {
        color: $dark-4 !important;
    }
    &.na_score {
        background-color: $dark-4;

        .label {
            color: $light !important;
        }
    }

    // options
    &.tooltip-option {
        border-left: 0.1rem solid $light-3;

        .label {
            color: $light;
        }
        .value {
            font-size: 13pt;
            font-weight: var(--font-weight-bold);
        }
    }

    &.well-card-option {
        cursor: pointer;

        .duc-badge {
            width: 1.5rem;
            height: 1.5rem;

            .bg {
                border-width: 0.75rem;
            }
            span {
                font-size: 8px;
                top: 1px;
                left: 3px;
            }
        }

        .label {
            padding: 0 2px;
            font-size: 10.5px;
            font-weight: 400;
        }
        .value {
            font-size: 14px;
            font-weight: var(--font-weight-bold);
        }
        &.rag-red-text {
            border: 0.1rem solid $red;
            background-color: $dark-3 !important;
        }

        &.rag-amber-text {
            border: 0.1rem solid $orange;
            background-color: $dark-3 !important;
        }

        &.rag-green {
            border: 0.1rem solid $green;

            span {
                color: $dark-4 !important;
            }
        }
    }
    &.table-cell-option {
        height: 3.5rem;
        background-color: transparent;
        cursor: pointer;

        .label {
            display: none;
        }
        .value {
            font-size: 10pt;
            font-weight: var(--font-weight-bold);
        }
        &.rag-red-text {
            border: 0.1rem solid $red;
            background-color: $dark-3 !important;
        }
        &.rag-amber-text {
            border: 0.1rem solid $orange;
            background-color: $dark-3 !important;
        }
        &.rag-green {
            border: 0.1rem solid $green;
        }
    }
    &.focused-well-option {
        border-radius: $border-radius;
        cursor: pointer;

        .label {
            font-size: 10pt;
            font-weight: var(--font-weight-regular);
        }
        .value {
            font-size: 18pt;
            font-weight: var(--font-weight-bold);
        }
        &.rag-red-text {
            border: 0.1rem solid $red;
            background-color: $dark-3 !important;
        }
        &.rag-amber-text {
            border: 0.1rem solid $orange;
            background-color: $dark-3 !important;
        }
        &.rag-green {
            border: 0.1rem solid $green;
        }
    }
}
</style>
